//brevoEmailService.js
import axios from 'axios';

const BREVO_API = 'https://api.brevo.com/v3/smtp/email';
const HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Api-Key': process.env.REACT_APP_BREVO_API_KEY
};

/**
 * Schedule ALL warranty reminders (30/7/0 days before expiry) via Brevo.
 *
 * This replaces the old Firestore + EmailJS pipeline
 * (reminderScheduler.js + reminderChecker.js + reminderActions.js).
 *
 * Returns an array of { label, daysBeforeExpiry, scheduledAt, messageId }
 * (or [] on failure / no expiry).
 */
export async function scheduleWarrantyReminders(asset) {
  if (!asset?.warrantyExpiry || !asset?.createdByEmail) {
    console.warn('[Warranty] Missing warrantyExpiry or createdByEmail — skipping.');
    return [];
  }

  // Normalise warrantyExpiry into a Date
  let warrDate;
  try {
    warrDate = asset.warrantyExpiry?.toDate
      ? asset.warrantyExpiry.toDate()
      : new Date(asset.warrantyExpiry);
  } catch (e) {
    console.error('[Warranty] Invalid warrantyExpiry:', e);
    return [];
  }

  if (Number.isNaN(warrDate.getTime())) {
    console.warn('[Warranty] Could not parse warrantyExpiry:', asset.warrantyExpiry);
    return [];
  }

  const now = new Date();

  // Skip entirely if warranty already expired
  if (warrDate <= now) {
    console.log('[Warranty] Already expired — no reminders scheduled.');
    return [];
  }

  const offsets = [
    { days: 30, label: '30 days before expiry' },
    { days: 7,  label: '7 days before expiry'  },
    { days: 0,  label: 'Expires today'         },
  ];

  const scheduled = [];

  for (const { days, label } of offsets) {
    const ideal = new Date(warrDate.getTime() - days * 24 * 60 * 60 * 1000);

    // If ideal date already passed, push to +1 hour from now
    const scheduledAt = ideal > now
      ? ideal
      : new Date(now.getTime() + 60 * 60 * 1000);

    const payload = {
      sender: {
        name:  process.env.REACT_APP_SENDER_NAME,
        email: process.env.REACT_APP_SENDER_EMAIL,
      },
      to: [{ email: asset.createdByEmail }],
      subject: `⚠️ Warranty ${label} – ${asset.assetName || 'Asset'}`,
      htmlContent: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #d97706;">Warranty Reminder</h2>
          <p>Your asset <b>${asset.assetName || 'Asset'}</b> (ID: ${asset.assetId || '—'})
             reaches warranty end on <b>${warrDate.toLocaleDateString()}</b>.</p>

          <p style="padding: 12px; background: #fef3c7; border-radius: 6px;
                    border: 1px solid #fbbf24;">
            <b>Reminder:</b> ${label}
          </p>

          <p>Please renew or replace it in time.</p>

          <p style="margin-top: 32px; color: #6b7280; font-size: 12px;">
            This is an automated notification from Niyantrix Asset Management.
          </p>
        </div>
      `,
      scheduledAt: scheduledAt.toISOString(),
    };

    try {
      const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
      scheduled.push({
        label,
        daysBeforeExpiry: days,
        scheduledAt: scheduledAt.toISOString(),
        messageId: res.data.messageId,
      });
      console.log(`[Warranty] Scheduled "${label}" for ${asset.assetId}:`, res.data.messageId);
    } catch (err) {
      console.error(`[Warranty] Failed to schedule "${label}":`, err?.response?.data || err);
      // Continue to next offset — don't abort the loop
    }
  }

  return scheduled;
}

export async function cancelWarrantyEmail(messageId) {
  if (!messageId) return;
  try {
    await axios.delete(`${BREVO_API}/${messageId}`, { headers: HEADERS });
  } catch (e) {
    console.warn('Cannot cancel e-mail:', e?.response?.data || e);
  }
}

/** Send an immediate "request received" e-mail (user or admin). */
export async function sendAccessRequestAck({ name = '', email, role = 'user' }) {
  if (!email) return null;

  const payload = {
    sender: {
      name: process.env.REACT_APP_SENDER_NAME,
      email: process.env.REACT_APP_SENDER_EMAIL
    },
    to: [{ email }],
    subject: `We received your ${role} access request`,
    htmlContent: `
      <p>Hello ${name || 'there'},</p>
      <p>Your request for <b>${role}</b> access has been received and is now under review.</p>
      <p>You will receive another e-mail with login credentials once it is approved.</p>
      <p style="margin-top:32px">Thanks,<br/>Niyantrix Team</p>`
  };

  try {
    const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
    return res.data.messageId || null;
  } catch (err) {
    console.warn('Ack e-mail failed:', err?.response?.data || err);
    return null;
  }
}

/** Send the final approval e-mail that contains the temporary password. */
export async function sendAccessApprovalEmail({
  name = '',
  email,
  role = 'user',
  password,
  companyName = ''
}) {
  if (!email || !password) return null;

  const payload = {
    sender: {
      name: process.env.REACT_APP_SENDER_NAME,
      email: process.env.REACT_APP_SENDER_EMAIL
    },
    to: [{ email }],
    subject: `Your ${role} access has been approved – welcome!`,
    htmlContent: `
      <p>Hello ${name || 'there'},</p>
      <p>Your request for <b>${role}</b> access has been approved.</p>
      <p><b>Username:</b> ${email}<br/><b>Password:</b> ${password}</p>
      <p>Please log in and change this temporary password immediately.</p>
      <p style="margin-top:32px">Welcome aboard${companyName ? ` at ${companyName}` : ''}!<br/>
         Niyantrix Team</p>`
  };

  try {
    const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
    return res.data.messageId || null;
  } catch (err) {
    console.warn('Approval e-mail failed:', err?.response?.data || err);
    return null;
  }
}

/** Send rejection e-mail when request is rejected */
export async function sendAccessRejectionEmail({
  name = '',
  email,
  role = 'user',
  reason = '',
  requestType = 'user'
}) {
  if (!email) return null;

  const guidance = requestType === 'admin'
    ? `
    <p><b>Next steps for admin access:</b></p>
    <ul>
      <li>Verify that your company email is correct</li>
      <li>Ensure you have proper authorization for admin access</li>
      <li>Check that all required company information was provided correctly</li>
    </ul>
    <p>If you believe this rejection was in error, please contact our Dedicated Support: <b>admin@infapp.biz</b> with your company details and business justification.</p>
    `
    : `
    <p><b>Next steps for user access:</b></p>
    <ul>
      <li>Verify your Company ID with your administrator</li>
      <li>Ensure you provided the correct company information</li>
      <li>Contact your company's admin to verify your department/role</li>
    </ul>
    <p>If you need assistance, please reach out to your company administrator or our Dedicated Support: <b>admin@infapp.biz</b></p>
    `;

  const payload = {
    sender: {
      name: process.env.REACT_APP_SENDER_NAME,
      email: process.env.REACT_APP_SENDER_EMAIL
    },
    to: [{ email }],
    subject: `Your Access Request t Niyantrix Has Been Rejected`,
    htmlContent: `
      <p>Hello ${name || 'there'},</p>
      <p>We regret to inform you that your request for <b>${role}</b> access to Niyantrix has been rejected.</p>
      ${reason ? `<p><b>Reason:</b> ${reason}</p>` : ''}

      ${guidance}

      <p style="margin-top:32px">
        If you have any questions or need further assistance, please don't hesitate to contact us.
      </p>
      <p>Best regards,<br/>Niyantrix Team</p>`
  };

  try {
    const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
    console.log(' Rejection email sent successfully:', res.data);
    return res.data.messageId || null;
  } catch (err) {
    console.error(' Rejection e-mail failed:', err?.response?.data || err);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// APPROVAL WORKFLOW EMAILS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Notify the current-level approver that an asset is awaiting their review.
 */
export async function sendApprovalRequestEmail({
  approverEmail,
  approverName = '',
  assetId,
  assetName,
  levelLabel = 'Approver',
  level = 1,
  requesterName = '',
  requesterEmail = '',
  companyName = '',
  comments = ''
}) {
  if (!approverEmail) {
    console.warn('[Approval] No approver email — skipping.');
    return null;
  }

  const appUrl = process.env.REACT_APP_APP_URL || 'https://app.niyantrix.com';

  const payload = {
    sender: {
      name: process.env.REACT_APP_SENDER_NAME,
      email: process.env.REACT_APP_SENDER_EMAIL
    },
    to: [{ email: approverEmail, name: approverName || approverEmail }],
    subject: `[Action Required] Asset "${assetName}" awaiting your approval (Level ${level})`,
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Asset Approval Required</h2>
        <p>Hello <b>${approverName || 'there'}</b>,</p>
        <p>An asset has been submitted for your review as <b>${levelLabel}</b> (Level ${level}).</p>

        <table style="border-collapse: collapse; width: 100%; margin: 16px 0;">
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Asset ID</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${assetId || '—'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Asset Name</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${assetName || '—'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Requested By</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${requesterName || '—'} (${requesterEmail || '—'})</td>
          </tr>
          ${companyName ? `
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Company</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${companyName}</td>
          </tr>` : ''}
        </table>

        ${comments ? `<p><b>Requester's comments:</b><br/><i>${comments}</i></p>` : ''}

        <p>Please log in to review and approve or reject this asset.</p>

        <p style="margin-top: 32px;">
          <a href="${appUrl}" style="background: #2563eb; color: #fff; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            Review Asset
          </a>
        </p>

        <p style="margin-top: 32px; color: #6b7280; font-size: 12px;">
          This is an automated notification from Niyantrix Asset Management.
        </p>
      </div>
    `
  };

  try {
    const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
    console.log(`[Approval] Level ${level} notification sent:`, res.data.messageId);
    return res.data.messageId || null;
  } catch (err) {
    console.error('[Approval] Level notification failed:', err?.response?.data || err);
    return null;
  }
}

/**
 * Notify the NEXT-level approver after current level approves.
 * Also CC's the creator so they know progress is being made.
 */
export async function sendNextLevelApprovalEmail({
  nextApproverEmail,
  nextApproverName = '',
  assetId,
  assetName,
  levelLabel = 'Approver',
  level = 2,
  previousApproverName = '',
  previousApproverEmail = '',
  previousLevelLabel = '',
  creatorEmail = '',
  creatorName = '',
  companyName = ''
}) {
  if (!nextApproverEmail) {
    console.warn('[Approval] No next-level approver email — skipping.');
    return null;
  }

  const appUrl = process.env.REACT_APP_APP_URL || 'https://niyantrix.in/';

  const payload = {
    sender: {
      name: process.env.REACT_APP_SENDER_NAME,
      email: process.env.REACT_APP_SENDER_EMAIL
    },
    to: [{ email: nextApproverEmail, name: nextApproverName || nextApproverEmail }],
    subject: `[Action Required] Asset "${assetName}" awaiting your approval (Level ${level})`,
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Asset Approval — Next Level</h2>
        <p>Hello <b>${nextApproverName || 'there'}</b>,</p>
        <p>The asset below has been approved by the previous level and now requires your review as <b>${levelLabel}</b> (Level ${level}).</p>

        <table style="border-collapse: collapse; width: 100%; margin: 16px 0;">
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Asset ID</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${assetId || '—'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Asset Name</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${assetName || '—'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Approved By</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">
              ${previousApproverName || '—'} ${previousLevelLabel ? `(${previousLevelLabel})` : ''}
            </td>
          </tr>
        </table>

        <p>Please log in to review and approve or reject this asset.</p>

        <p style="margin-top: 32px;">
          <a href="${appUrl}" style="background: #2563eb; color: #fff; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            Review Asset
          </a>
        </p>

        <p style="margin-top: 32px; color: #6b7280; font-size: 12px;">
          This is an automated notification from Niyantrix Asset Management.
        </p>
      </div>
    `
  };

  if (creatorEmail) {
    payload.cc = [{ email: creatorEmail, name: creatorName || creatorEmail }];
  }

  try {
    const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
    console.log(`[Approval] Level ${level} notification sent:`, res.data.messageId);
    return res.data.messageId || null;
  } catch (err) {
    console.error('[Approval] Next-level notification failed:', err?.response?.data || err);
    return null;
  }
}

/**
 * Notify the CREATOR when the workflow completes (all levels approved)
 * or when any level rejects.
 */
export async function sendApprovalStatusEmail({
  creatorEmail,
  creatorName = '',
  assetId,
  assetName,
  status = 'approved', // 'approved' | 'rejected' | 'correction'
  approverName = '',
  approverEmail = '',
  levelLabel = '',
  comments = '',
  companyName = ''
}) {
  if (!creatorEmail) return null;

  const appUrl = process.env.REACT_APP_APP_URL || 'https://app.niyantrix.com';

  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isCorrection = status === 'correction';

  const statusColor = isApproved ? '#059669' : isRejected ? '#dc2626' : '#d97706';
  const statusText = isApproved ? 'APPROVED' : isRejected ? 'REJECTED' : 'CORRECTION REQUESTED';
  const headline = isApproved
    ? `Your asset "${assetName}" has been fully approved`
    : isRejected
      ? `Your asset "${assetName}" was rejected`
      : `Correction requested for asset "${assetName}"`;

  const payload = {
    sender: {
      name: process.env.REACT_APP_SENDER_NAME,
      email: process.env.REACT_APP_SENDER_EMAIL
    },
    to: [{ email: creatorEmail, name: creatorName || creatorEmail }],
    subject: `[${statusText}] Asset "${assetName}"`,
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: ${statusColor};">${statusText}</h2>
        <p>Hello <b>${creatorName || 'there'}</b>,</p>
        <p>${headline}.</p>

        <table style="border-collapse: collapse; width: 100%; margin: 16px 0;">
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Asset ID</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${assetId || '—'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Asset Name</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${assetName || '—'}</td>
          </tr>
          ${approverName ? `
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; background: #f9fafb;"><b>Action By</b></td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">
              ${approverName} ${levelLabel ? `(${levelLabel})` : ''}
            </td>
          </tr>` : ''}
        </table>

        ${comments ? `<p><b>Comments:</b><br/><i>${comments}</i></p>` : ''}

        <p style="margin-top: 32px;">
          <a href="${appUrl}" style="background: ${statusColor}; color: #fff; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            View Asset
          </a>
        </p>

        <p style="margin-top: 32px; color: #6b7280; font-size: 12px;">
          This is an automated notification from Niyantrix Asset Management.
        </p>
      </div>
    `
  };

  try {
    const res = await axios.post(BREVO_API, payload, { headers: HEADERS });
    console.log(`[Approval] Status email (${status}) sent:`, res.data.messageId);
    return res.data.messageId || null;
  } catch (err) {
    console.error('[Approval] Status email failed:', err?.response?.data || err);
    return null;
  }
}